// @flow

// $FlowFixMe
import {
  createContext,
  // $FlowFixMe
  useCallback,
  // $FlowFixMe
  useContext,
  // $FlowFixMe
  useEffect,
  // $FlowFixMe
  useMemo,
  // $FlowFixMe
  useRef,
  // $FlowFixMe
  useState
} from "react";
import { find, get, throttle } from "lodash";
import buildRaceUrl from "@tvg/formatter/url";
import type {
  HandicapSortOptionsType,
  RaceCardBettingInterest,
  RaceProgram,
  Results
} from "@tvg/types/Race";
import type { PastPerformanceRace } from "@fdr/types/PastPerformance";
import type { State } from "@fdr/types/ProgramPage";
import sortRunnerList from "@tvg/utils/handicappingSort";

const AppContext = createContext({});

const getActions = (setState) => ({
  goNextRace: () =>
    setState((state) => ({ ...state, raceNumber: +state.raceNumber + 1 })),
  goLastRace: () =>
    setState((state) => ({ ...state, raceNumber: +state.raceNumber - 1 }))
});

const useAppContext = () => useContext(AppContext);

const useGlobalState = (iniState: State) => {
  const [state, setState] = useState({ ...iniState });

  const actions = useMemo(() => getActions(setState), [setState]);

  return { state, actions };
};

const getPosition = () =>
  typeof window !== "undefined" ? window.pageYOffset : 0;

const useWindowScrollPosition = (throttleValue: number) => {
  const [position, setPosition] = useState(getPosition());

  useEffect(() => {
    const handleScroll = throttle(() => {
      setPosition(getPosition());
    }, throttleValue);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      handleScroll.cancel();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return position;
};

function useLastValue(value: mixed) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const useNextRaceUrl = (
  races: RaceProgram[],
  race: RaceProgram | PastPerformanceRace
) => {
  const [forwardDisabled, setForwardDisabled] = useState(false);
  const [nextRace, setNextRace] = useState(race.raceNumber);
  const [nextButtonLink, setNextRaceUrl] = useState("");

  const setRace = (raceNumber: string) => {
    const link = buildRaceUrl(
      get(race, "track.trackCode", ""),
      get(race, "track.trackName", ""),
      raceNumber
    );
    setNextRace(raceNumber);
    setNextRaceUrl(link);
  };

  useEffect(() => {
    if (races && races.length > 0) {
      const racePosition = races.findIndex(
        (raceItem) => raceItem.id === race.id
      );

      if (racePosition === races.length - 1) {
        setForwardDisabled(true);
      } else {
        setForwardDisabled(false);
      }

      if (racePosition !== -1 && racePosition !== races.length - 1) {
        setRace(String(races[racePosition + 1].raceNumber));
      } else {
        setRace(race.raceNumber || `${get(races, "length", "1")}`);
      }
    }
  }, [race.raceNumber, race.id, JSON.stringify(races)]);

  return { nextRace, forwardDisabled, nextButtonLink };
};

const usePreviousRaceUrl = (
  races: RaceProgram[],
  race: RaceProgram | PastPerformanceRace
) => {
  const [backDisabled, setBackDisabled] = useState(false);
  const [previousRace, setPreviousRace] = useState(race.raceNumber);
  const [backButtonLink, setPreviousRaceUrl] = useState("");

  const setRace = (raceNumber: string) => {
    const link = buildRaceUrl(
      get(race, "track.trackCode", ""),
      get(race, "track.trackName", ""),
      raceNumber
    );
    setPreviousRace(raceNumber);
    setPreviousRaceUrl(link);
  };

  useEffect(() => {
    if (races) {
      const racePosition = races.findIndex(
        (raceItem) => raceItem.id === race.id
      );

      if (racePosition === 0) {
        setBackDisabled(true);
      } else {
        setBackDisabled(false);
      }

      if (racePosition !== -1 && racePosition !== 0) {
        setRace(String(races[racePosition - 1].raceNumber));
      } else {
        setRace(race.raceNumber || "1");
      }
    }
  }, [race.raceNumber, race.id]);

  return { previousRace, backDisabled, backButtonLink };
};

const usePendingResults = (code: string, results: ?Results) => {
  const [isResultsPending, setResultsPending] = useState(false);

  useEffect(() => {
    if (code === "SK") {
      setResultsPending(true);
    } else {
      setResultsPending(false);
    }
  }, [code, JSON.stringify(results)]);

  return isResultsPending;
};

const useBettingInsterestsOrder = (
  bettingInterests: RaceCardBettingInterest[][],
  id: string,
  sortingMode: HandicapSortOptionsType,
  active: boolean
): RaceCardBettingInterest[][] => {
  const [oddsOrder, setOddsOrder] = useState([]);

  // reset odds order
  useEffect(() => {
    setOddsOrder([]);
  }, [sortingMode]);

  // change order on race change
  useEffect(() => {
    if (sortingMode === "odds") {
      setOddsOrder([]);
    }
  }, [id]);

  // set odds order on bettingInterests when changes
  useEffect(() => {
    if (
      bettingInterests.length &&
      (!oddsOrder.length || oddsOrder.length !== bettingInterests.length) &&
      sortingMode === "odds" &&
      active
    ) {
      setOddsOrder(
        bettingInterests.map((entry) =>
          sortRunnerList(entry, "basic", "odds").map((runner) =>
            get(runner, "runners[0].runnerId")
          )
        ) || []
      );
    }
  }, [JSON.stringify(bettingInterests), active]);

  if (
    sortingMode === "odds" &&
    oddsOrder.length &&
    oddsOrder.length === bettingInterests.length &&
    active
  ) {
    return (bettingInterests || []).map((entry, index) => {
      const bettingInterestsScratched = [];
      const validbettingInterests = [];

      entry.forEach((bi) => {
        if (bi.runners && bi.runners.length > 1) {
          bi.runners.forEach((currentRunner) => {
            const runner = {
              ...bi,
              runners: [currentRunner]
            };

            if (currentRunner.scratched) {
              bettingInterestsScratched.push(runner);
            } else {
              validbettingInterests.push(runner);
            }
          });
        } else if (bi.runners && bi.runners[0].scratched) {
          bettingInterestsScratched.push(bi);
        } else {
          validbettingInterests.push(bi);
        }
      });

      const finalOrder = oddsOrder[index]
        .map((bi) =>
          find(
            validbettingInterests,
            (value) => bi === get(value, "runners[0].runnerId")
          )
        )
        .filter(Boolean);

      return finalOrder.concat(bettingInterestsScratched);
    });
  }

  return (bettingInterests || []).map((entry) =>
    sortRunnerList(entry, "basic", sortingMode || "odds")
  );
};

const MINUTE_IN_MS = 60000;
const useCurrentRaceAvailable = (races: RaceProgram[]) => {
  const [currentRaceAvailable, setCurrentRaceAvailable] =
    useState<RaceProgram>();
  const [intervalId, setIntervalId] = useState<TimeoutID>(null);

  const getAvailableRace = useCallback(
    () =>
      (races || []).find((race) => {
        const code = get(race, "status.code");
        return code !== "RO";
      }),
    [races]
  );

  useMemo(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }

    const interval = get(currentRaceAvailable, "mtp") || 0.5;
    setCurrentRaceAvailable(getAvailableRace());

    const id = setInterval(() => {
      setCurrentRaceAvailable(getAvailableRace());
    }, interval * MINUTE_IN_MS); // Transforming Milliseconds in minutes;

    setIntervalId(id);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [races, currentRaceAvailable]);

  return currentRaceAvailable;
};

export {
  AppContext,
  useGlobalState,
  useAppContext,
  useWindowScrollPosition,
  useLastValue,
  useNextRaceUrl,
  usePreviousRaceUrl,
  usePendingResults,
  useBettingInsterestsOrder,
  useCurrentRaceAvailable
};
